import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { estMobileEnvoyable } from '../../services/sms';
import { libelleStatutDepuis } from '../../utils/statutAtelier';
import DemandeNumeroOr from './DemandeNumeroOr';
import { Plaque, PHASES, dateCourte, duree, etatPieces, idDossier, normaliser, phaseStatut } from './planningMobileUtils';

// ═══════════════════════════════════════════════════════════════════════════
// Dossier atelier — version téléphone (2026-10-23).
//
// Une colonne, sans onglets : véhicule et client en haut (Appeler, SMS), le
// statut juste dessous, le reste replié avec un résumé visible même fermé.
// Plein écran plutôt que modale : on y entre et on en sort par « ‹ Liste »,
// comme une page de téléphone — rien ne se ferme d'un geste involontaire.
//
// Gestes proposés : statut, commentaire, SMS. Ce qui demande la grille ou un
// long formulaire (replanifier, travaux, signature, courtoisie, Datacar) reste
// sur ordinateur ; « Version ordinateur » rouvre CE dossier dans la grille.
//
// Mêmes routes et mêmes verrous que la grille et la modale :
//   - statut : capacité `dossier.statut_atelier`, liste réduite aux trois
//     statuts du compagnon sans `dossier.statut_atelier_tous` (verrou serveur
//     identique) ; n° d'OR réclamé ensuite s'il manque (DemandeNumeroOr) ;
//   - passage en « Facturé » : le SMS « véhicule prêt » est proposé, comme sur
//     la grille ;
//   - SMS relu par l'IA avant l'envoi, sans « Envoyer mon texte » (décision du
//     2026-10-01 : la retouche se fait dans la version relue).
// ═══════════════════════════════════════════════════════════════════════════

// Statuts autorisés au rôle Technicien (même liste que le verrou serveur et la grille).
const estStatutTechnicien = (nom) => {
  const cle = normaliser(nom);
  return cle === 'en intervention' || cle === 'diag en cours' || cle === 'diag termine';
};

const LIVRAISON = {
  livre: 'Reçue',
  en_commande: 'Commandée',
  recu_partiellement: 'Reçue en partie',
};

const quand = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const auj = new Date();
  if (d.toDateString() === auj.toDateString()) return h;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${h}`;
};

function Bloc({ titre, compte, resume, ouvertParDefaut, children }) {
  return (
    <details className="pm-bloc" open={ouvertParDefaut}>
      <summary>
        <span>{titre}</span>
        {compte != null && <span className="pm-bloc-compte">{compte}</span>}
        {resume && <span className="pm-bloc-resume">{resume}</span>}
      </summary>
      <div className="pm-bloc-corps">{children}</div>
    </details>
  );
}

export default function DossierMobile({
  ligne, liste, onNaviguer, statuts, refs, peut, lectureSeuleDroits, onFerme, onModifie, onVersionOrdinateur,
}) {
  const id = idDossier(ligne);
  const [dossier, setDossier] = useState(null);
  const [complements, setComplements] = useState([]);
  const [commentaires, setCommentaires] = useState([]);
  const [carnet, setCarnet] = useState(null);
  const [erreur, setErreur] = useState('');
  const [vue, setVue] = useState('fiche'); // fiche | statut | sms
  const [envoiStatut, setEnvoiStatut] = useState(null);
  const [demandeOr, setDemandeOr] = useState(false);
  const [texteCommentaire, setTexteCommentaire] = useState('');
  const [envoiCommentaire, setEnvoiCommentaire] = useState(false);
  const [message, setMessage] = useState('');
  const [modeles, setModeles] = useState(null);
  const [sms, setSms] = useState({ texte: '', relecture: null, erreur: '', occupe: false });
  const [ouvrirSmsPret, setOuvrirSmsPret] = useState(false);

  // Ce qui est montré avant le chargement : la ligne de la liste suffit pour
  // l'en-tête, le dossier complet arrive ensuite.
  const d = dossier || ligne;

  const charger = useCallback(async (signal) => {
    try {
      const r = await api.get(`/planning/interventions/${id}`, { signal });
      const intv = r.data?.intervention || null;
      setDossier(intv);
      setErreur('');
      const [comp, com] = await Promise.allSettled([
        api.get(`/planning/interventions/${id}/complements`, { signal }),
        api.get(`/planning/interventions/${id}/commentaires`, { signal }),
      ]);
      setComplements(comp.status === 'fulfilled' ? (comp.value.data?.complements || []) : []);
      setCommentaires(com.status === 'fulfilled' ? (com.value.data?.commentaires || []) : []);
      if (intv?.numero_or && peut('pieces.carnet')) {
        api.get(`/carnet-commande/by-or/${encodeURIComponent(intv.numero_or)}`, { signal })
          .then(c => setCarnet(c.data?.lignes || []))
          .catch(() => setCarnet([]));
      } else {
        setCarnet(null);
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      setErreur('Le dossier ne s’est pas chargé. Vérifiez la connexion et réessayez.');
    }
  }, [id, peut]);

  useEffect(() => {
    const ctrl = new AbortController();
    setDossier(null); setComplements([]); setCommentaires([]); setCarnet(null);
    setVue('fiche'); setMessage('');
    charger(ctrl.signal);
    return () => ctrl.abort();
  }, [charger]);

  // Haut de page à chaque dossier : on ne garde pas le défilement du précédent.
  const defilement = useRef(null);
  useEffect(() => { if (defilement.current) defilement.current.scrollTop = 0; }, [id, vue]);

  const minuterieMessage = useRef(null);
  useEffect(() => () => clearTimeout(minuterieMessage.current), []);
  const montrer = (texte) => {
    setMessage(texte);
    clearTimeout(minuterieMessage.current);
    minuterieMessage.current = setTimeout(() => setMessage(''), 2600);
  };

  const position = liste.findIndex(l => idDossier(l) === id && l.id === ligne.id);
  const precedent = position > 0 ? liste[position - 1] : null;
  const suivant = position >= 0 && position < liste.length - 1 ? liste[position + 1] : null;

  const peutStatut = !lectureSeuleDroits && peut('dossier.statut_atelier');
  const peutTous = peut('dossier.statut_atelier_tous');
  const peutCommenter = !lectureSeuleDroits && peut('dossier.commentaire');
  const tel = d.client_tel || '';
  const peutSms = !lectureSeuleDroits && peut('client.sms_envoyer') && estMobileEnvoyable(tel);
  const client = d.client || [d.client_prenom, d.client_nom].filter(Boolean).join(' ') || 'Client non renseigné';
  const vehicule = [d.marque, d.modele].filter(Boolean).join(' ');
  const statutCourant = statuts.find(s => s.id === d.statut_atelier_id);

  // ── Statut ────────────────────────────────────────────────────────────────
  const choisirStatut = async (statutId) => {
    if (envoiStatut) return;
    if (statutId === (d.statut_atelier_id || null)) { setVue('fiche'); return; }
    setEnvoiStatut(statutId ?? 'aucun');
    try {
      await api.patch(`/planning/interventions/${id}/statut-atelier`, { statut_atelier_id: statutId });
      const s = statuts.find(x => x.id === statutId);
      setDossier(prev => ({
        ...(prev || ligne),
        statut_atelier_id: statutId,
        statut_atelier_nom: s?.nom || null,
        statut_atelier_couleur: s?.couleur || null,
        statut_atelier_since: new Date().toISOString(),
        statut_atelier_since_estime: false,
      }));
      setVue('fiche');
      montrer(`Statut enregistré : ${s?.nom || 'aucun'}`);
      onModifie();
      const sansOr = !String(d.numero_or || '').trim();
      const facture = normaliser(s?.nom) === 'facture';
      if (statutId && sansOr && peut('dossier.numero_or')) {
        setDemandeOr(true);
        setOuvrirSmsPret(facture && peutSms);
      } else if (facture && peutSms) {
        ouvrirSms('pret');
      }
    } catch (e) {
      montrer(e.response?.data?.error || 'Statut refusé par le serveur.');
    } finally {
      setEnvoiStatut(null);
    }
  };

  // ── Commentaire ───────────────────────────────────────────────────────────
  const envoyerCommentaire = async (e) => {
    e.preventDefault();
    const texte = texteCommentaire.trim();
    if (!texte || envoiCommentaire) return;
    setEnvoiCommentaire(true);
    try {
      const r = await api.post(`/planning/interventions/${id}/commentaires`, { texte });
      setCommentaires(prev => [...prev, r.data.commentaire]);
      setTexteCommentaire('');
      montrer('Commentaire ajouté');
    } catch (err) {
      montrer(err.response?.data?.error || 'Commentaire non enregistré.');
    } finally {
      setEnvoiCommentaire(false);
    }
  };

  // ── SMS ───────────────────────────────────────────────────────────────────
  const ouvrirSms = (intention) => {
    setSms({ texte: '', relecture: null, erreur: '', occupe: false });
    setVue('sms');
    const appliquer = (liste) => {
      if (intention !== 'pret') return;
      const m = liste.find(x => normaliser(x.libelle).includes('pret'));
      if (m) setSms(s => ({ ...s, texte: m.contenu || '' }));
    };
    if (modeles !== null) { appliquer(modeles); return; }
    api.get(`/planning/interventions/${id}/messages`)
      .then(r => {
        const liste = (r.data.messages || []).filter(m => m.canal === 'sms' || m.canal === 'les_deux');
        setModeles(liste);
        appliquer(liste);
      })
      .catch(() => setModeles([]));
  };

  const envoyerSms = async (texteChoisi) => {
    const texte = String(texteChoisi || '').trim();
    if (!texte) return;
    setSms(s => ({ ...s, occupe: true, erreur: '' }));
    try {
      await api.post(`/planning/interventions/${id}/sms`, { message: texte });
      setVue('fiche');
      montrer('SMS envoyé');
    } catch (err) {
      setSms(s => ({ ...s, occupe: false, erreur: err.response?.data?.error || 'Envoi impossible.' }));
    }
  };

  const relireEtEnvoyer = async () => {
    const texte = sms.texte.trim();
    if (!texte || sms.occupe) return;
    setSms(s => ({ ...s, occupe: true, erreur: '' }));
    let relecture;
    try {
      const { data } = await api.post(`/planning/interventions/${id}/sms/corriger`, { message: texte });
      relecture = data;
    } catch (e) {
      setSms(s => ({ ...s, occupe: false, relecture: { indispo: true } }));
      return;
    }
    if (relecture.identique) { envoyerSms(texte); return; }
    setSms(s => ({ ...s, occupe: false, relecture: { texte: relecture.texte, remarques: relecture.remarques || [] } }));
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  const travaux = Array.isArray(dossier?.travaux) && dossier.travaux.length
    ? dossier.travaux
    : [{ libelle: d.type_intervention || d.description || 'Travaux non précisés', duree: d.duree }];
  const dureeTotale = travaux.reduce((n, t) => n + (Number(t.duree) || 0), 0);
  const pieces = etatPieces(d);
  const resumePieces = carnet && carnet.length
    ? (pieces === 'disponible' ? 'toutes reçues' : pieces === 'en_commande' ? 'en attente' : pieces === 'indisponible' ? 'une indisponible' : `${carnet.length} ligne${carnet.length > 1 ? 's' : ''}`)
    : (d.numero_or ? 'aucune commande' : 'n° d’OR requis');
  const depuis = libelleStatutDepuis(d.statut_atelier_nom, d.statut_atelier_since, d.statut_atelier_since_estime);

  const enTete = (titre, retour) => (
    <div className="pm-dossier-barre">
      <button type="button" className="pm-retour" onClick={retour}>‹ {vue === 'fiche' ? 'Liste' : 'Dossier'}</button>
      <span className="pm-dossier-titre">{titre}</span>
      {vue === 'fiche' ? (
        <>
          <button type="button" className="pm-nav" disabled={!precedent} onClick={() => onNaviguer(precedent)} aria-label="Dossier précédent">‹</button>
          <button type="button" className="pm-nav" disabled={!suivant} onClick={() => onNaviguer(suivant)} aria-label="Dossier suivant">›</button>
        </>
      ) : <span className="pm-nav-vide" />}
    </div>
  );

  if (vue === 'statut') {
    const options = statuts.filter(s => peutTous || estStatutTechnicien(s.nom));
    let phaseVue = null;
    return (
      <div className="pm-dossier" role="dialog" aria-label="Changer le statut">
        {enTete('Statut atelier', () => setVue('fiche'))}
        <div className="pm-dossier-defile" ref={defilement}>
          <div className="pm-options">
            {peutTous && (
              <button type="button" className={`pm-option${!d.statut_atelier_id ? ' is-actif' : ''}`} onClick={() => choisirStatut(null)} disabled={!!envoiStatut}>
                <span className="pm-option-pastille pm-option-pastille--vide" />Aucun statut
              </button>
            )}
            {options.map(s => {
              const p = phaseStatut(s.id, s.nom, refs);
              const titre = p !== phaseVue ? PHASES.find(x => x.cle === p)?.libelle : null;
              phaseVue = p;
              return (
                <React.Fragment key={s.id}>
                  {titre && <div className="pm-options-groupe">{titre}</div>}
                  <button
                    type="button"
                    className={`pm-option${d.statut_atelier_id === s.id ? ' is-actif' : ''}`}
                    onClick={() => choisirStatut(s.id)}
                    disabled={!!envoiStatut}
                  >
                    <span className="pm-option-pastille" style={{ background: s.couleur }} />
                    {s.nom}
                    {envoiStatut === s.id && <span className="pm-option-envoi">Enregistrement…</span>}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {message && <div className="pm-message" role="status">{message}</div>}
      </div>
    );
  }

  if (vue === 'sms') {
    const texteFinal = sms.relecture?.texte ?? sms.texte;
    return (
      <div className="pm-dossier" role="dialog" aria-label="Envoyer un SMS">
        {enTete('SMS au client', () => setVue('fiche'))}
        <div className="pm-dossier-defile" ref={defilement}>
          <div className="pm-sms">
            <p className="pm-sms-dest">À {client} · {tel}</p>
            {modeles && modeles.length > 0 && (
              <div className="pm-sms-modeles">
                {modeles.map(m => (
                  <button type="button" key={m.id} className="pm-pill pm-pill--bouton" onClick={() => setSms(s => ({ ...s, texte: m.contenu || '', relecture: null, erreur: '' }))}>
                    {m.libelle}
                  </button>
                ))}
              </div>
            )}
            <label htmlFor="pm-sms-texte" className="pm-etiquette">Message</label>
            <textarea
              id="pm-sms-texte"
              className="pm-saisie"
              rows={5}
              maxLength={320}
              value={sms.texte}
              readOnly={!!sms.relecture?.texte}
              onChange={(e) => setSms(s => ({ ...s, texte: e.target.value, relecture: null, erreur: '' }))}
              placeholder="Choisissez un modèle ou écrivez le message"
            />
            <p className="pm-aide">Les SMS partent sans accents. La signature de la concession est ajoutée si vous ne signez pas.</p>
            {sms.relecture?.texte && (
              <div className="pm-sms-relecture">
                <label htmlFor="pm-sms-relu" className="pm-etiquette">Version relue par l’IA, modifiable avant l’envoi</label>
                <textarea
                  id="pm-sms-relu"
                  className="pm-saisie"
                  rows={5}
                  maxLength={320}
                  value={sms.relecture.texte}
                  onChange={(e) => setSms(s => ({ ...s, relecture: { ...s.relecture, texte: e.target.value } }))}
                />
                {sms.relecture.remarques.length > 0 && <p className="pm-aide">{sms.relecture.remarques.join(' · ')}</p>}
              </div>
            )}
            {sms.relecture?.indispo && <p className="pm-aide pm-aide--alerte">La relecture IA n’a pas répondu. Vous pouvez envoyer votre texte tel quel.</p>}
            {sms.erreur && <p className="pm-aide pm-aide--erreur">{sms.erreur}</p>}
            <p className="pm-aide">{texteFinal.length}/160{texteFinal.length > 160 ? ' : partira en plusieurs SMS' : ''}</p>
          </div>
        </div>
        <div className="pm-dossier-pied">
          <button type="button" className="pm-bouton" onClick={() => setVue('fiche')} disabled={sms.occupe}>Annuler</button>
          {sms.relecture?.texte ? (
            <button type="button" className="pm-bouton pm-bouton--principal" onClick={() => envoyerSms(sms.relecture.texte)} disabled={sms.occupe || !sms.relecture.texte.trim()}>
              {sms.occupe ? 'Envoi…' : 'Envoyer la version relue'}
            </button>
          ) : sms.relecture?.indispo ? (
            <button type="button" className="pm-bouton pm-bouton--principal" onClick={() => envoyerSms(sms.texte)} disabled={sms.occupe || !sms.texte.trim()}>
              {sms.occupe ? 'Envoi…' : 'Envoyer tel quel'}
            </button>
          ) : (
            <button type="button" className="pm-bouton pm-bouton--principal" onClick={relireEtEnvoyer} disabled={sms.occupe || !sms.texte.trim()}>
              {sms.occupe ? 'Relecture…' : 'Envoyer'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pm-dossier" role="dialog" aria-label={`Dossier ${d.immat || d.immatriculation || ''}`}>
      {enTete(d.numero_or ? `OR ${d.numero_or}` : 'OR à préparer', onFerme)}
      <div className="pm-dossier-defile" ref={defilement}>
        {erreur && <div className="pm-vide pm-vide--erreur">{erreur}</div>}

        <div className="pm-entete">
          <div className="pm-entete-vehicule">
            <Plaque immat={d.immat || d.immatriculation} />
            <span>{[vehicule, d.kilometrage ? `${Number(d.kilometrage).toLocaleString('fr-FR')} km` : ''].filter(Boolean).join(' · ')}</span>
          </div>
          <div className="pm-entete-client">{client}</div>
          {(tel || peutSms) && (
            <div className="pm-actions">
              {tel && (
                <a className="pm-action" href={`tel:${tel.replace(/[^\d+]/g, '')}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>
                  Appeler
                </a>
              )}
              {peutSms && (
                <button type="button" className="pm-action" onClick={() => ouvrirSms()}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                  SMS
                </button>
              )}
            </div>
          )}
        </div>

        {d.client_annule_at && (
          <div className="pm-alerte">Annulé par le client le {quand(d.client_annule_at)} via le lien du SMS, à rappeler.</div>
        )}

        <button
          type="button"
          className="pm-statut-bouton"
          style={{ '--pm-statut': d.statut_atelier_couleur || 'var(--border-strong)' }}
          onClick={() => peutStatut && setVue('statut')}
          disabled={!peutStatut}
        >
          <span className="pm-statut-pastille" />
          <b>{d.statut_atelier_nom || statutCourant?.nom || 'Aucun statut'}</b>
          {peutStatut && <span className="pm-statut-changer">Changer</span>}
          <small>{depuis || 'statut atelier'}</small>
        </button>

        <Bloc titre="Rendez-vous" resume={[d.heure_reception, d.receptionnaire_nom].filter(Boolean).join(' · ')} ouvertParDefaut>
          <dl className="pm-kv">
            <dt>Réception</dt><dd>{d.date_reception ? `${dateCourte(d.date_reception)}${d.heure_reception ? ` à ${d.heure_reception}` : ''}` : 'non renseignée'}</dd>
            {d.receptionnaire_nom && (<><dt>Réceptionnaire</dt><dd>{d.receptionnaire_nom}</dd></>)}
            <dt>Restitution prévue</dt><dd>{d.date_restitution_prevue ? `${dateCourte(d.date_restitution_prevue)}${d.heure_restitution_prevue ? ` à ${d.heure_restitution_prevue}` : ''}` : 'non renseignée'}</dd>
            {d.restituee_at && (<><dt>Restitué</dt><dd>{quand(d.restituee_at)}</dd></>)}
            {d.client_attend && (<><dt>Client</dt><dd>attend sur place</dd></>)}
            {d.a_courtoisie && (<><dt>Courtoisie</dt><dd>véhicule prêté</dd></>)}
          </dl>
        </Bloc>

        <Bloc titre="Travaux" compte={travaux.length + complements.length} resume={duree(dureeTotale)} ouvertParDefaut>
          {travaux.map((t, k) => (
            <div className="pm-travail" key={`t${k}`}>
              <b>{t.libelle || 'Travail'}</b>
              <span>{duree(t.duree)}</span>
              {k === 0 && <small>{[d.mecanicien, d.date_intervention ? dateCourte(d.date_intervention) : '', d.heure_debut].filter(Boolean).join(' · ')}</small>}
            </div>
          ))}
          {complements.map(c => (
            <div className="pm-travail" key={`c${c.id}`}>
              <b>Suite{c.commentaire ? ` : ${c.commentaire}` : ''}</b>
              <span>{duree(c.duree)}</span>
              <small>{[c.mecanicien, dateCourte(c.date_intervention), c.heure_debut].filter(Boolean).join(' · ')}</small>
            </div>
          ))}
        </Bloc>

        {peut('pieces.carnet') && (
          <Bloc titre="Pièces" compte={carnet ? carnet.length : null} resume={resumePieces}>
            {!d.numero_or && <p className="pm-aide">Les pièces sont suivies par n° d’OR : ce dossier n’en a pas encore.</p>}
            {d.numero_or && carnet === null && <p className="pm-aide">Chargement…</p>}
            {carnet && carnet.length === 0 && <p className="pm-aide">Aucune ligne au carnet de commande pour cet OR.</p>}
            {carnet && carnet.map((p, k) => (
              <div className="pm-piece" key={`${p.reference}-${k}`}>
                <span>{p.description || 'Pièce'}</span>
                <code>{p.reference}</code>
                <small>
                  {LIVRAISON[p.statut_livraison] || p.statut_livraison || 'Statut inconnu'}
                  {p.cde_qty ? ` · ${p.qte_livree || 0}/${p.cde_qty} livrée${p.cde_qty > 1 ? 's' : ''}` : ''}
                  {p.statut_livraison !== 'livre' && p.sifa?.date_eta ? ` · arrivée prévue ${dateCourte(p.sifa.date_eta)}` : ''}
                </small>
              </div>
            ))}
          </Bloc>
        )}

        <Bloc titre="Commentaires" compte={commentaires.length} ouvertParDefaut={commentaires.length > 0}>
          {commentaires.map(c => (
            <div className="pm-commentaire" key={c.id}>
              <small>{[c.user_prenom, c.user_nom].filter(Boolean).join(' ') || 'Utilisateur'} · {quand(c.created_at)}</small>
              {c.texte}
            </div>
          ))}
          {commentaires.length === 0 && !peutCommenter && <p className="pm-aide">Aucun commentaire.</p>}
          {peutCommenter && (
            <form className="pm-commentaire-saisie" onSubmit={envoyerCommentaire}>
              <input
                id="pm-commentaire"
                value={texteCommentaire}
                onChange={(e) => setTexteCommentaire(e.target.value)}
                placeholder="Ajouter un commentaire"
                autoComplete="off"
                enterKeyHint="send"
              />
              <button type="submit" disabled={!texteCommentaire.trim() || envoiCommentaire}>{envoiCommentaire ? '…' : 'Envoyer'}</button>
            </form>
          )}
        </Bloc>

        <p className="pm-aide pm-aide--centre">
          Replanifier, travaux, signature, courtoisie, Datacar : sur la version ordinateur.
        </p>
      </div>

      <div className="pm-dossier-pied">
        <button type="button" className="pm-bouton" onClick={() => onVersionOrdinateur && onVersionOrdinateur(id)}>Version ordinateur</button>
        {peutStatut && (
          <button type="button" className="pm-bouton pm-bouton--principal" onClick={() => setVue('statut')}>Changer le statut</button>
        )}
      </div>

      {message && <div className="pm-message" role="status">{message}</div>}

      {demandeOr && (
        <DemandeNumeroOr
          dossier={{ ...d, id }}
          onFerme={() => { setDemandeOr(false); if (ouvrirSmsPret) { setOuvrirSmsPret(false); ouvrirSms('pret'); } }}
          onEnregistre={(num) => {
            setDemandeOr(false);
            setDossier(prev => ({ ...(prev || ligne), numero_or: num }));
            onModifie();
            if (ouvrirSmsPret) { setOuvrirSmsPret(false); ouvrirSms('pret'); }
          }}
        />
      )}
    </div>
  );
}
